import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { SMLLink } from '../../components/SMLLink/SMLLink';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';
import { isNavItemActive, smlHomeNavItem, SMLNavGroup, SMLNavGroupId } from '../smlNavigation';
import { useVisibleNavGroups } from '../hooks/useVisibleNavGroups';
import { SMLNavBadges } from '../hooks/useNavBadges';
import { smlNavigationChromeMessages as messages } from '../smlNavigationMessages';
import styles from './MobileBottomBar.module.scss';

interface MobileBottomBarProps {
  screen: SMLRouteNames | undefined;
  /** The group of the page on screen; its tab is lit. */
  currentGroup: SMLNavGroup | undefined;
  openGroupId: SMLNavGroupId | undefined;
  sheetId: string;
  badges: SMLNavBadges;
  params?: Record<string, string>;
  onToggleGroup: (groupId: SMLNavGroupId) => void;
  onNavigateHome: () => void;
}

/**
 * Design b1: fixed items — Home and the groups (Outreach is the fourth group, design outreach a1). A group tap opens its hub sheet. The
 * lit tab is the open sheet's group, or else the group of the page on screen. A red pip marks a
 * group that holds an attention count.
 */
const MobileBottomBarComponent: React.FC<MobileBottomBarProps> = ({
  screen,
  currentGroup,
  openGroupId,
  sheetId,
  badges,
  params,
  onToggleGroup,
  onNavigateHome,
}) => {
  const intl = useIntl();
  const groups = useVisibleNavGroups();
  const isHome = isNavItemActive(smlHomeNavItem, screen);
  const litGroupId = openGroupId ?? currentGroup?.id;

  return (
    <nav className={styles.bar} aria-label={intl.formatMessage(messages.groupsNav)}>
      <SMLLink
        to={smlHomeNavItem.route}
        params={params}
        className={clsx(styles.tab, isHome && openGroupId === undefined && styles.on)}
        ariaCurrent={isHome ? 'page' : undefined}
        onNavigate={onNavigateHome}
      >
        <span className={styles.pill}>
          <FontAwesomeIcon icon={smlHomeNavItem.icon} />
        </span>
        {intl.formatMessage(smlHomeNavItem.label)}
      </SMLLink>
      {groups.map((group) => {
        const isOpen = openGroupId === group.id;
        return (
          <button
            key={group.id}
            type="button"
            className={clsx(styles.tab, litGroupId === group.id && styles.on)}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls={isOpen ? sheetId : undefined}
            // The group of the page on screen, so a screen reader hears where the page belongs.
            aria-current={currentGroup?.id === group.id ? 'true' : undefined}
            onClick={() => onToggleGroup(group.id)}
            data-testid={`mobile-tab-${group.id}`}
          >
            <span className={styles.pill}>
              <FontAwesomeIcon icon={group.icon} />
              {badges.groupsWithAttention.includes(group.id) && <span className={styles.pip} aria-hidden="true" />}
            </span>
            {intl.formatMessage(group.tabLabel)}
          </button>
        );
      })}
    </nav>
  );
};

export const MobileBottomBar = TypedMemo(MobileBottomBarComponent);
